// Master Yi Alpha Strike VFX: 4 rapid sequential slash lines per target, chaining across enemies.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const SLASHES_PER_TARGET = 4;
const SLASH_INTERVAL = 75;  // ms between each slash start
const SLASH_DURATION = 170; // ms each slash line is visible
const TARGET_GAP = 110;     // ms pause between targets

// Each target takes: SLASH_INTERVAL * (SLASHES_PER_TARGET - 1) + SLASH_DURATION
// = 75 * 3 + 170 = 395ms
// 3 targets total: 395 + 110 + 395 + 110 + 395 = 1405ms

// Angles for the 4 slashes (relative to movement direction), creating an X-fan pattern
const SLASH_OFFSETS = [
  Math.PI / 2 + 0.4,
  Math.PI / 2 - 0.15,
  Math.PI / 2 + 0.15,
  Math.PI / 2 - 0.4,
];

interface SlashEvent {
  startTime: number;
  center: Point;
  slashAngle: number;
  targetIndex: number;
  isLast: boolean;
}

export class AlphaStrikeVFX implements Animation {
  private elapsed = 0;
  private readonly totalDuration: number;
  private readonly events: SlashEvent[] = [];
  private readonly firedCallbacks = new Set<number>();

  onStart?: () => void;
  onEnd?: () => void;

  constructor(
    private readonly targets: Point[],
    private readonly angle: number,
    private readonly size: number,
    private readonly onTargetComplete?: (targetIndex: number) => void,
  ) {
    let time = 0;
    for (let ti = 0; ti < targets.length; ti++) {
      for (let si = 0; si < SLASHES_PER_TARGET; si++) {
        this.events.push({
          startTime: time,
          center: targets[ti],
          slashAngle: angle + SLASH_OFFSETS[si],
          targetIndex: ti,
          isLast: si === SLASHES_PER_TARGET - 1,
        });
        time += SLASH_INTERVAL;
      }
      time += TARGET_GAP;
    }
    this.totalDuration = time - TARGET_GAP - SLASH_INTERVAL + SLASH_DURATION;
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, this.totalDuration);

    for (const ev of this.events) {
      if (!ev.isLast || this.firedCallbacks.has(ev.targetIndex)) continue;
      if (this.elapsed >= ev.startTime + SLASH_DURATION * 0.75) {
        this.firedCallbacks.add(ev.targetIndex);
        this.onTargetComplete?.(ev.targetIndex);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const ev of this.events) {
      const local = this.elapsed - ev.startTime;
      if (local < 0 || local > SLASH_DURATION) continue;

      const t = local / SLASH_DURATION;
      const alpha = t < 0.2 ? t / 0.2 : 1 - (t - 0.2) / 0.8;
      if (alpha <= 0) continue;

      const len = this.size * 1.25 * Easing.easeOutQuad(Math.min(t / 0.25, 1));

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(ev.center.x, ev.center.y);
      ctx.rotate(ev.slashAngle);
      ctx.lineCap = "round";

      // Glow layer
      ctx.strokeStyle = "#80e8ff";
      ctx.lineWidth = 3.5;
      ctx.shadowColor = "#00ccff";
      ctx.shadowBlur = 20;
      ctx.beginPath();
      ctx.moveTo(-len, 0);
      ctx.lineTo(len, 0);
      ctx.stroke();

      // Bright white core
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.moveTo(-len * 0.6, 0);
      ctx.lineTo(len * 0.6, 0);
      ctx.stroke();

      ctx.restore();
    }
  }

  isDone(): boolean {
    return this.elapsed >= this.totalDuration;
  }
}

// Backwards-compat alias so CombatAnimator import name stays stable
export { AlphaStrikeVFX as SlashVFX };
